import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell } from "../components/PageShell";
import nigelPhoto from "../assets/nigel-clarion.png";

export const Route = createFileRoute("/nigel")({
  head: () => ({
    meta: [
      { title: "Nigel P. Chase — The Pan Institute" },
      {
        name: "description",
        content:
          "Nigel P. Chase — Steel Band Musician, Teacher, Arranger, Composer, Consultant, Pan Builder and Tuner. Founder of Pan Institute and Pan Coalition; Director, Atlantic Clarion Steel Band.",
      },
    ],
  }),
  component: NigelPage,
});

const ROLES = [
  "Steel Band Musician",
  "Teacher",
  "Arranger",
  "Composer",
  "Consultant",
  "Pan Builder & Tuner",
];

const EDUCATION = [
  { degree: "B.A.", field: "Music Theory and Composition", school: "University of New Hampshire" },
  { degree: "M.A.", field: "Orchestral Conducting", school: "Aaron Copeland School of Music, NY" },
];

const AFFILIATIONS = [
  { role: "Founder", org: "Pan Institute" },
  { role: "Founder", org: "Pan Coalition" },
  { role: "Director", org: "Atlantic Clarion Steel Band" },
];

function NigelPage() {
  return (
    <PageShell
      title={
        <>
          Nigel P. <span className="text-primary">Chase</span>
        </>
      }
      intro="Founder of the Pan Institute. Musician, teacher, composer, arranger, builder and tuner — a life shaped around the steelpan."
    >
      {/* Big centered portrait with short flanking captions */}
      <div className="mt-8 grid md:grid-cols-12 gap-8 md:gap-16 items-center">
        <aside className="relative z-20 md:col-span-4 order-2 md:order-1 space-y-6">
          <p className="font-display italic text-xl md:text-2xl leading-snug text-foreground/85">
            The first pan ever made in Maine was hammered out for him as a
            four-year-old.
          </p>
          <div className="text-[10px] uppercase tracking-[0.4em] text-primary/80">
            Brooksville, 1974
          </div>
          <p className="font-display italic text-xl md:text-2xl leading-snug text-foreground/85 pt-4 border-t border-border/40">
            Director, Atlantic Clarion Steel Band — and the institute that
            grew around it.
          </p>
          <div className="text-[10px] uppercase tracking-[0.4em] text-primary/80">
            Maine · ongoing
          </div>
        </aside>

        <figure className="relative z-10 md:col-span-8 order-1 md:order-2">
          <img
            src={nigelPhoto}
            alt="Nigel P. Chase with an Atlantic Clarion steel pan"
            className="block w-full h-auto select-none drop-shadow-[0_50px_90px_rgba(0,0,0,0.6)] mx-auto"
            draggable={false}
          />
        </figure>
      </div>

      {/* Dossier — two columns, dense */}
      <div className="mt-20 rounded-3xl border border-border/40 bg-card/30 backdrop-blur-sm p-6 md:p-10">
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-10">

          {/* Organizations */}
          <div>
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.4em] text-primary/90">
                Organizations
              </div>
            </div>
            <ul className="space-y-2.5">
              {AFFILIATIONS.map((a) => (
                <li key={a.org} className="flex items-baseline gap-3">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-primary/80 w-16 shrink-0">
                    {a.role}
                  </span>
                  <span className="font-display italic text-lg md:text-xl text-foreground/90 leading-tight">
                    {a.org}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Education */}
          <div>
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.4em] text-primary/90">
                Education
              </div>
            </div>
            <ul className="space-y-3">
              {EDUCATION.map((e) => (
                <li key={e.degree + e.school} className="flex items-baseline gap-3">
                  <span className="font-display italic text-2xl text-primary font-light w-12 shrink-0">
                    {e.degree}
                  </span>
                  <div className="min-w-0">
                    <div className="text-base text-foreground/90 leading-tight">
                      {e.field}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-foreground/50 mt-1">
                      {e.school}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-24 rounded-3xl border border-border/40 bg-gradient-to-br from-primary/15 via-card/40 to-transparent p-8 md:p-12">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
              Want to learn or play?
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl leading-[1.05]">
              Get in touch with Nigel and the Institute.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-7 py-3 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/20 whitespace-nowrap"
            >
              Contact us
            </Link>
          </div>
        </div>
      </div>

      {/* Charter CTA */}
      <div className="mt-12 rounded-3xl border border-border/40 bg-gradient-to-br from-primary/15 via-card/40 to-transparent p-8 md:p-12">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
              Now booking the season
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl leading-[1.05]">
              Nigel is also captain of the Alamar.
            </h2>
            <p className="mt-3 text-foreground/70 text-sm md:text-base max-w-md">
              A traditional wooden schooner offering sailing charters along the coast of Maine.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href="https://www.sailalamar.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-7 py-3 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/20 whitespace-nowrap"
            >
              Book a Charter
            </a>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
